import interact from "interactjs";
import {
	DropOperation,
	DropOperationEventType,
	DropOperationFactory,
} from "./types";

export class ElementDropZoneHandler {
	private _dropZoneElement: HTMLElement;
	private _dropOperationFactories: DropOperationFactory<HTMLElement>[];

	private _eventTypeMap: Partial<Record<string, DropOperationEventType>> = {
		dragenter: "dragEnter",
		dropmove: "dragOver",
		dragleave: "dragLeave",
		drop: "drop",
	};

	private _draggableElementOperations = new WeakMap<
		HTMLElement,
		DropOperation
	>();

	constructor(
		dropZoneElement: HTMLElement,
		dropOperationFactories: DropOperationFactory<HTMLElement>[],
	) {
		this._dropZoneElement = dropZoneElement;
		this._dropOperationFactories = dropOperationFactories;

		interact(this._dropZoneElement).dropzone({
			ondragenter: this._onEvent,
			ondropmove: this._onEvent,
			ondragleave: this._onEvent,
			ondrop: this._onEvent,
			checker: this._validateDropZoneEvent,
		});
	}

	private _onEvent = (event: Interact.DragEvent | Interact.DropEvent) => {
		const type = this._eventTypeMap[event.type];
		if (!type) {
			return;
		}

		const element = event.relatedTarget as HTMLElement;
		let operation = this._draggableElementOperations.get(element);

		if (!operation && event.type === "dragenter") {
			operation = this._dropOperationFactories.reduce(
				(prev, factory) => prev ?? factory(element),
				undefined as DropOperation | undefined,
			);
			if (operation) {
				this._draggableElementOperations.set(element, operation);
			}
		}

		if (operation) {
			const dragEvent = ["dragenter", "dropmove"].includes(event.type)
				? (event as Interact.DropEvent).dragEvent
				: (event as Interact.DragEvent);
			const finished = operation.onEvent({
				type,
				x: dragEvent.clientX,
				y: dragEvent.clientY,
			});
			if (finished) {
				this._draggableElementOperations.delete(element);
			}
		}
	};

	private _validateDropZoneEvent = (
		_dragEvent: unknown,
		event: Interact.DragEvent,
		dropped: boolean,
	) => {
		const hoveredElement = document.elementFromPoint(
			event.clientX,
			event.clientY,
		);
		const isOverDropZone = hoveredElement === this._dropZoneElement;
		return dropped && isOverDropZone;
	};
}
